import {ChevronDown} from 'lucide-react';
import { useState } from "react";

export default function UnitedStatesCountry() {
  const timeline = [
    { label: "Check BON requirements", description: "Confirm eligibility, fees, and background checks" },
    { label: "Submit application", description: "Send transcripts/verification as required" },
    { label: "Receive endorsement", description: "Update employer and begin onboarding" }
  ];

  const faqItems = [
    {
      q: "Do I need to retake NCLEX to endorse?",
      a: "Generally no; endorsement recognizes your existing license. Requirements vary by state."
    },
    {
      q: "Can I change employers during endorsement?",
      a: "Often yes, depending on visa/employment terms. Confirm with HR and immigration counsel."
    }
  ];

  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Hero */}
      <section className="bg-gradient-to-br from-teal-600 to-teal-700 text-white py-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">United States</h1>
          <p className="text-xl text-teal-50">
            License endorsement & career advancement
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="max-w-4xl mx-auto px-6 py-16 space-y-12">
        {/* Intro Text */}
        <div className="prose prose-lg max-w-none">
          <p className="text-gray-700 leading-relaxed">
            If you're already in the U.S., your path focuses on license <strong>endorsement</strong>, direct-hire opportunities, and career advancement.
          </p>
        </div>

        {/* Core Actions */}
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Core actions</h2>
          <ul className="space-y-3">
            <li className="flex items-start">
              <span className="text-teal-600 mr-3 mt-1">•</span>
              <span className="text-gray-700">
                <strong>License Endorsement:</strong> transfer RN license between states (check your State BON)
              </span>
            </li>
            <li className="flex items-start">
              <span className="text-teal-600 mr-3 mt-1">•</span>
              <span className="text-gray-700">
                <strong>Employer Transfers:</strong> direct-hire hospitals and reputable recruiters
              </span>
            </li>
            <li className="flex items-start">
              <span className="text-teal-600 mr-3 mt-1">•</span>
              <span className="text-gray-700">
                <strong>Documentation:</strong> active RN license, immunization & medicals, background checks, employment letters
              </span>
            </li>
          </ul>
        </div>

        {/* Timeline */}
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Typical endorsement flow</h2>
          <div className="space-y-6">
            {timeline.map((step, index) => (
              <div key={index} className="flex items-start">
                <div className="flex-shrink-0 w-48">
                  <span className="inline-block px-3 py-1 bg-teal-100 text-teal-700 rounded-full text-sm font-semibold">
                    {step.label}
                  </span>
                </div>
                <p className="text-gray-700 ml-4">{step.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* FAQ */}
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
          <div className="border border-gray-200 rounded-lg divide-y divide-gray-200">
            {faqItems.map((item, index) => (
              <div key={index}>
                <button
                  onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
                  className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
                >
                  <span className="font-semibold text-gray-900">{item.q}</span>
                  <ChevronDown
                    className={`w-5 h-5 text-gray-500 transition-transform ${
                      openFaqIndex === index ? "rotate-180" : ""
                    }`}
                  />
                </button>
                {openFaqIndex === index && (
                  <div className="px-6 py-4 bg-gray-50 text-gray-700 leading-relaxed">
                    {item.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-8">
          <a
            href="/signup"
            className="px-8 py-3 bg-teal-600 text-white rounded-lg font-semibold hover:bg-teal-700 transition-colors"
          >
            Register / Login
          </a>
          <a
            href="/faq"
            className="px-8 py-3 border-2 border-gray-300 text-gray-700 rounded-lg font-semibold hover:border-gray-400 hover:bg-gray-50 transition-colors"
          >
            FAQ
          </a>
        </div>
      </section>
    </div>
  );
}
